"use client";

import { useQuery } from "@apollo/client/react";
import { batch, signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { useQuery as useTanstackQuery } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import {
  createContext,
  startTransition,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import {
  type FocusProfileResponse,
  getNotificationsRequest,
  getUserSocialProfiles,
  NotificationStatus,
} from "@/backend/focus";
import { getMyRoles, USER_ROLES } from "@/backend/focus/admin-ui";
import {
  applyHiddenNotificationTypesToParams,
  getNotificationRequestFiltersFromLocalStorage,
} from "@/components/app/notifications/notifications-context";
import type { AuthContextType } from "@/contexts/auth-context";
import { useAuthContext, useAuthSignalState } from "@/contexts/auth-context";
import type { FilterRecord } from "@/contexts/filters-sort-context";
import type {
  Account,
  GlobalValQuery,
  UserAssociation,
} from "@/graphql/codegen/graphql";
import { UserAssociationsDocument } from "@/graphql/codegen/graphql";
import {
  AccountByPublicKeyDocument,
  GlobalValDocument,
  TotalUnclaimedMessageTipsDocument,
} from "@/graphql/codegen/graphql";
import { useBackgroundPathname } from "@/hooks/use-background-pathname";
import type { UseDerivedKeyReturnValue } from "@/hooks/use-derived-key";
import { useDerivedKey } from "@/hooks/use-derived-key";
import { useFocusProfileQuery } from "@/hooks/use-focus-profile-query";
import { useIdleReady } from "@/hooks/use-idle-ready";
import { useUserLists } from "@/hooks/use-user-lists";
import type { ProfileSocials } from "@/utils/constants";
import { APP_ROUTES, AssociationType } from "@/utils/constants";
import {
  getFocusExtraDataString,
  parseFocusPreferences,
} from "@/utils/focus-preferences";
import { getPollInterval } from "@/utils/helpers";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import { coerceProfileExtraData } from "@/utils/profile-extra-data";
import {
  CURRENT_USER_SOCIAL_PROFILES_QUERY_KEY,
  USER_ROLES_CACHE_MAX_AGE_MS,
  USER_ROLES_QUERY_KEY,
  USER_SOCIAL_PROFILES_CACHE_MAX_AGE_MS,
} from "@/utils/query-persistence";

type UserContextType = AuthContextType & {
  user: Account | null;
  refetchUser: () => void;
  userLoading: boolean;
  loading: boolean;
  userSocialProfilesLoading: boolean;
  userSocialProfiles?: UserSocialProfiles;
  refetchUserSocialProfiles: () => void;
  alternateUsers: { [publicKey: string]: { publicKey: string } };
  wealth: WealthObject | null;
  isAdmin: boolean;
  isModerator: boolean;
  isElevated: boolean;
  isSuperAdmin: boolean;
  isVerifier: boolean;
  focusProfile: FocusProfileResponse | null;
  refetchFocusProfile: () => void;

  realTimeDesoBalanceNanos: number;
  realTimeDusdBalanceNanos: number;
  realTimeDusdBaseUnits: string; // original value to be used for MAX button and other calculations
  realTimeFocusUnlockedBalanceNanos: number;
  realTimeFocusUnlockedBaseUnits: string; // original value to be used for MAX button and other calculations
  realTimeFocusLockedBalanceNanos: number;
  realTimeFocusLockedBaseUnits: string; // original value to be used for MAX button and other calculations

  setUnreadNotifications: Dispatch<SetStateAction<number>>;
  unreadNotifications: number;
  totalUnclaimedMessageTipsUsdCents: number;
  unreadTotalMessagesCount: number;
  setUnreadTotalMessagesCount: Dispatch<SetStateAction<number>>;

  blockedUsers: UserAssociation[];
  isUserBlocked: (publicKey?: string) => boolean;
  addUserToBlockedUsers: (publicKey: string) => void;
  removeUserFromBlockedUsers: (publicKey: string) => void;
  toggleBlockUser: (publicKey: string) => void;

  generalDerivedKey: UseDerivedKeyReturnValue["derivedKey"];
  createGeneralDerivedKey: UseDerivedKeyReturnValue["create"];
  generalDerivedKeyStatus: UseDerivedKeyReturnValue["status"];
  clearGeneralDerivedKey: UseDerivedKeyReturnValue["clear"];
  authorizeGeneralDerivedKey: UseDerivedKeyReturnValue["authorize"];

  globalVal: NonNullable<GlobalValQuery["globalValByRowId"]>;
} & ReturnType<typeof useUserLists>;

export const UserContext = createContext<UserContextType>(null!);

type UserContextProps = {
  children?: ReactNode;
};

type WealthObject = {
  desoBalanceUsdCents: number;
  focusTokenBalanceUsdCents: number;
  usdBalanceUsdCents: number;
  totalBalanceUsdCents?: number;
};

type UserBalancesSignalState = {
  realTimeDesoBalanceNanos: number;
  realTimeDusdBalanceNanos: number;
  realTimeDusdBaseUnits: string;
  realTimeFocusUnlockedBalanceNanos: number;
  realTimeFocusUnlockedBaseUnits: string;
  realTimeFocusLockedBalanceNanos: number;
  realTimeFocusLockedBaseUnits: string;
};

const DEFAULT_USER_BALANCES: UserBalancesSignalState = {
  realTimeDesoBalanceNanos: 0,
  realTimeDusdBalanceNanos: 0,
  realTimeDusdBaseUnits: "0",
  realTimeFocusUnlockedBalanceNanos: 0,
  realTimeFocusUnlockedBaseUnits: "0",
  realTimeFocusLockedBalanceNanos: 0,
  realTimeFocusLockedBaseUnits: "0",
};

export const userAccountSignal = signal<Account | null>(null);
export const userLoadingSignal = signal(false);
export const userSocialProfilesLoadingSignal = signal(false);
export const userSocialProfilesSignal = signal<UserSocialProfiles | undefined>(
  undefined,
);
export const focusProfileSignal = signal<FocusProfileResponse | null>(null);
export const userBalancesSignal = signal<UserBalancesSignalState>({
  ...DEFAULT_USER_BALANCES,
});
export const userWealthSignal = signal<WealthObject | null>(null);
export const blockedUsersSignal = signal<UserAssociation[]>([]);
export const unreadNotificationsSignal = signal(0);
export const unreadTotalMessagesCountSignal = signal(0);
export const totalUnclaimedMessageTipsUsdCentsSignal = signal(0);

let setUnreadNotificationsStateBridge:
  | ((value: SetStateAction<number>) => void)
  | null = null;
let setUnreadTotalMessagesCountStateBridge:
  | ((value: SetStateAction<number>) => void)
  | null = null;

const resolveSignalSetState = <T,>(
  nextValue: SetStateAction<T>,
  previousValue: T,
) => {
  return typeof nextValue === "function"
    ? (nextValue as (previousValue: T) => T)(previousValue)
    : nextValue;
};

export const setUnreadNotificationsSignal = (
  nextValue: SetStateAction<number>,
) => {
  const resolvedValue = resolveSignalSetState(
    nextValue,
    unreadNotificationsSignal.value,
  );

  unreadNotificationsSignal.value = resolvedValue;
  setUnreadNotificationsStateBridge?.(resolvedValue);
};

export const setUnreadTotalMessagesCountSignal = (
  nextValue: SetStateAction<number>,
) => {
  const resolvedValue = resolveSignalSetState(
    nextValue,
    unreadTotalMessagesCountSignal.value,
  );

  unreadTotalMessagesCountSignal.value = resolvedValue;
  setUnreadTotalMessagesCountStateBridge?.(resolvedValue);
};

export const useUserAuthSignalState = useAuthSignalState;

export const useUserAccountSignalState = () => {
  useSignals();

  const userLoading = userLoadingSignal.value;
  const userSocialProfilesLoading = userSocialProfilesLoadingSignal.value;

  return {
    focusProfile: focusProfileSignal.value,
    loading: userLoading || userSocialProfilesLoading,
    user: userAccountSignal.value,
    userLoading,
    userSocialProfiles: userSocialProfilesSignal.value,
    userSocialProfilesLoading,
  };
};

export const useUserBalancesSignalState = () => {
  useSignals();

  return {
    ...userBalancesSignal.value,
    wealth: userWealthSignal.value,
  };
};

export const useUnreadNotificationsSignalState = () => {
  useSignals();

  return {
    setUnreadNotifications: setUnreadNotificationsSignal,
    unreadNotifications: unreadNotificationsSignal.value,
  };
};

export const useUnreadMessagesSignalState = () => {
  useSignals();

  return {
    setUnreadTotalMessagesCount: setUnreadTotalMessagesCountSignal,
    totalUnclaimedMessageTipsUsdCents:
      totalUnclaimedMessageTipsUsdCentsSignal.value,
    unreadTotalMessagesCount: unreadTotalMessagesCountSignal.value,
  };
};

export const useBlockedUsersSignalState = () => {
  useSignals();

  const blockedUsers = blockedUsersSignal.value;

  return {
    blockedUsers,
    isUserBlocked: (publicKey?: string) => {
      if (!publicKey) {
        return false;
      }

      return blockedUsers.some(
        (association) => association.target?.publicKey === publicKey,
      );
    },
  };
};

export type UserProfileSocialsValues =
  (typeof ProfileSocials)[keyof typeof ProfileSocials];

export type UserSocialProfiles = {
  [key in UserProfileSocialsValues]?: {
    Username?: string;
    Verified?: boolean;
    ProfilePicUrl?: string;
  };
};

export const UserProvider = ({ children }: UserContextProps) => {
  const {
    currentUserPublicKey,
    alternateUsers,
    loggedIn,
    setCurrentUserPublicKey,
  } = useAuthContext();
  const pathname = useBackgroundPathname();
  const router = useRouter();
  const idleLoggedInBootstrapReady = useIdleReady(loggedIn, {
    timeout: 1200,
  });
  const idleGlobalValReady = useIdleReady(true, {
    timeout: 1200,
  });

  const {
    data: accountByPublicKeyData,
    loading: accountByPublicKeyLoading,
    refetch: refetchUser,
  } = useQuery(AccountByPublicKeyDocument, {
    variables: {
      publicKey: currentUserPublicKey!,
    },
    fetchPolicy: "cache-and-network",
    skip: !loggedIn,
  });
  const fetchedUser = accountByPublicKeyData?.accountByPublicKey || null;
  const user: Account | null =
    loggedIn && fetchedUser?.publicKey === currentUserPublicKey
      ? (fetchedUser as Account)
      : null;
  const userLoading = loggedIn ? accountByPublicKeyLoading : false;

  const {
    data: userSocialProfiles,
    isLoading: userSocialProfilesLoading,
    refetch: refetchUserSocialProfiles,
  } = useTanstackQuery({
    queryKey: [CURRENT_USER_SOCIAL_PROFILES_QUERY_KEY, currentUserPublicKey],
    queryFn: () => {
      return getUserSocialProfiles(currentUserPublicKey!);
    },
    enabled: loggedIn && idleLoggedInBootstrapReady,
    gcTime: USER_SOCIAL_PROFILES_CACHE_MAX_AGE_MS,
    refetchOnWindowFocus: false,
    staleTime: USER_SOCIAL_PROFILES_CACHE_MAX_AGE_MS,
  });

  const {
    derivedKey: generalDerivedKey,
    create: createGeneralDerivedKey,
    status: generalDerivedKeyStatus,
    clear: clearGeneralDerivedKey,
    authorize: authorizeGeneralDerivedKey,
  } = useDerivedKey({ publicKey: currentUserPublicKey });
  const createGeneralDerivedKeyRef = useRef(createGeneralDerivedKey);
  createGeneralDerivedKeyRef.current = createGeneralDerivedKey;

  // DESO
  const [realTimeDesoBalanceNanos, setRealTimeDesoBalanceNanos] = useState(0);

  // DESO-USDC
  const [realTimeDusdBalanceNanos, setRealTimeDusdBalanceNanos] = useState(0);
  const [realTimeDusdBaseUnits, setRealTimeDusdBaseUnits] = useState("0");

  // FOCUS
  const [
    realTimeFocusUnlockedBalanceNanos,
    setRealTimeFocusUnlockedBalanceNanos,
  ] = useState(0);
  const [realTimeFocusUnlockedBaseUnits, setRealTimeFocusUnlockedBaseUnits] =
    useState("0");

  // FOCUS LOCKED
  const [realTimeFocusLockedBalanceNanos, setRealTimeFocusLockedBalanceNanos] =
    useState(0);
  const [realTimeFocusLockedBaseUnits, setRealTimeFocusLockedBaseUnits] =
    useState("0");

  const [wealth, setWealth] = useState<WealthObject | null>(null);
  const [blockedUsers, setBlockedUsers] = useState<UserAssociation[]>([]);
  const [unreadNotifications, setUnreadNotificationsState] = useState(0);
  const setUnreadNotifications = setUnreadNotificationsSignal;
  const [unreadTotalMessagesCount, setUnreadTotalMessagesCountState] =
    useState(0);
  const setUnreadTotalMessagesCount = setUnreadTotalMessagesCountSignal;
  const [notificationFiltersHydrated, setNotificationFiltersHydrated] =
    useState(false);

  const [notificationFilters] = useLocalStorage<FilterRecord[]>(
    `${LocalStorageKeys.notificationPageFilters}-filters`,
    [],
    {
      initializeWithValue: false,
    },
  );
  const hiddenNotificationTypes = useMemo(() => {
    return parseFocusPreferences(
      getFocusExtraDataString(coerceProfileExtraData(user?.extraData)),
      {
        loggedIn,
      },
    ).hiddenNotificationTypes;
  }, [loggedIn, user?.extraData]);
  const hiddenNotificationTypesKey = hiddenNotificationTypes.join(",");
  const notificationCountInputsHydrated =
    loggedIn && !!user && !userLoading && notificationFiltersHydrated;
  const userListsState = useUserLists(currentUserPublicKey, {
    enabled: !loggedIn || idleLoggedInBootstrapReady,
  });

  useEffect(() => {
    setUnreadNotificationsStateBridge = setUnreadNotificationsState;
    setUnreadTotalMessagesCountStateBridge = setUnreadTotalMessagesCountState;

    return () => {
      setUnreadNotificationsStateBridge = null;
      setUnreadTotalMessagesCountStateBridge = null;
    };
  }, []);

  useLayoutEffect(() => {
    batch(() => {
      userAccountSignal.value = user as Account | null;
      userLoadingSignal.value = userLoading;
      userSocialProfilesLoadingSignal.value = userSocialProfilesLoading;
      userSocialProfilesSignal.value = userSocialProfiles;
    });
  }, [user, userLoading, userSocialProfiles, userSocialProfilesLoading]);

  const { data: blockedUserAssociationsData } = useQuery(
    UserAssociationsDocument,
    {
      variables: {
        filter: {
          associationType: { equalTo: AssociationType.block },
          transactor: { publicKey: { equalTo: currentUserPublicKey } },
        },
      },
      skip: !loggedIn || !idleLoggedInBootstrapReady,
    },
  );

  // Update blocked users state
  // Future todo: use optimistic update instead
  useEffect(() => {
    const blockedUsers = blockedUserAssociationsData?.userAssociations
      ?.nodes as UserAssociation[];
    startTransition(() => {
      const nextBlockedUsers = blockedUsers ?? [];

      blockedUsersSignal.value = nextBlockedUsers;
      setBlockedUsers(nextBlockedUsers);
    });
  }, [blockedUserAssociationsData]);

  // Update balances
  useEffect(() => {
    if (!user) {
      startTransition(() => {
        batch(() => {
          userBalancesSignal.value = { ...DEFAULT_USER_BALANCES };
          userWealthSignal.value = null;
        });

        setRealTimeDesoBalanceNanos(
          DEFAULT_USER_BALANCES.realTimeDesoBalanceNanos,
        );
        setRealTimeDusdBalanceNanos(
          DEFAULT_USER_BALANCES.realTimeDusdBalanceNanos,
        );
        setRealTimeDusdBaseUnits(DEFAULT_USER_BALANCES.realTimeDusdBaseUnits);
        setRealTimeFocusUnlockedBalanceNanos(
          DEFAULT_USER_BALANCES.realTimeFocusUnlockedBalanceNanos,
        );
        setRealTimeFocusUnlockedBaseUnits(
          DEFAULT_USER_BALANCES.realTimeFocusUnlockedBaseUnits,
        );
        setRealTimeFocusLockedBalanceNanos(
          DEFAULT_USER_BALANCES.realTimeFocusLockedBalanceNanos,
        );
        setRealTimeFocusLockedBaseUnits(
          DEFAULT_USER_BALANCES.realTimeFocusLockedBaseUnits,
        );
        setWealth(null);
      });
      return;
    }

    const updatedDesoBalanceNanos = Number(
      user.accountWealth?.desoBalanceNanos ?? 0,
    );
    const updatedDUSDBalanceNanos =
      Number(user.accountWealth?.usdBalanceBaseUnits ?? 0) / 1e9;
    const updatedFocusUnlockedBalanceNanos =
      Number(user.accountWealth?.focusTokenBalanceBaseUnits ?? 0) / 1e9;

    const updatedFocusLockedBalanceNanos =
      Number(user.accountWealth?.focusTokenLockedBalanceBaseUnits ?? 0) / 1e9;

    // DESO
    startTransition(() => {
      const nextBalances = {
        realTimeDesoBalanceNanos: updatedDesoBalanceNanos,
        realTimeDusdBalanceNanos: updatedDUSDBalanceNanos,
        realTimeDusdBaseUnits: user.accountWealth?.usdBalanceBaseUnits ?? "0",
        realTimeFocusUnlockedBalanceNanos: updatedFocusUnlockedBalanceNanos,
        realTimeFocusUnlockedBaseUnits:
          user.accountWealth?.focusTokenBalanceBaseUnits ?? "0",
        realTimeFocusLockedBalanceNanos: updatedFocusLockedBalanceNanos,
        realTimeFocusLockedBaseUnits:
          user.accountWealth?.focusTokenLockedBalanceBaseUnits ?? "0",
      };
      const nextWealth = {
        desoBalanceUsdCents: user.accountWealth?.desoBalanceUsdCents || 0,
        focusTokenBalanceUsdCents:
          user.accountWealth?.focusTokenBalanceUsdCents || 0,
        usdBalanceUsdCents: user.accountWealth?.usdBalanceUsdCents || 0,
        totalBalanceUsdCents: user.accountWealth?.totalBalanceUsdCents || 0,
      };

      batch(() => {
        userBalancesSignal.value = nextBalances;
        userWealthSignal.value = nextWealth;
      });

      setRealTimeDesoBalanceNanos(nextBalances.realTimeDesoBalanceNanos);

      // DESO-USDC
      setRealTimeDusdBalanceNanos(nextBalances.realTimeDusdBalanceNanos);
      setRealTimeDusdBaseUnits(nextBalances.realTimeDusdBaseUnits);

      // FOCUS
      setRealTimeFocusUnlockedBalanceNanos(
        nextBalances.realTimeFocusUnlockedBalanceNanos,
      );
      setRealTimeFocusUnlockedBaseUnits(
        nextBalances.realTimeFocusUnlockedBaseUnits,
      );

      // FOCUS LOCKED
      setRealTimeFocusLockedBalanceNanos(
        nextBalances.realTimeFocusLockedBalanceNanos,
      );
      setRealTimeFocusLockedBaseUnits(
        nextBalances.realTimeFocusLockedBaseUnits,
      );

      setWealth(nextWealth);
    });
  }, [user]);

  const { data: unclaimedMessageTipsData } = useQuery(
    TotalUnclaimedMessageTipsDocument,
    {
      variables: {
        publicKey: currentUserPublicKey!,
      },
      skip: !loggedIn,
      pollInterval: getPollInterval(30000),
      notifyOnNetworkStatusChange: false,
    },
  );
  const totalUnclaimedMessageTipsUsdCents = Number(
    unclaimedMessageTipsData?.totalUnclaimedMessageTipByPublicKey
      ?.totalUnclaimedMessageTipsUsdCents || 0,
  );

  useEffect(() => {
    totalUnclaimedMessageTipsUsdCentsSignal.value =
      totalUnclaimedMessageTipsUsdCents;
  }, [totalUnclaimedMessageTipsUsdCents]);

  const {
    data: { isAdmin, isSuperAdmin, isModerator } = {
      isAdmin: false,
      isSuperAdmin: false,
      isModerator: false,
    },
  } = useTanstackQuery({
    queryKey: [USER_ROLES_QUERY_KEY, currentUserPublicKey],
    queryFn: async () => {
      const { Role } = await getMyRoles(currentUserPublicKey!);
      return {
        isAdmin: [USER_ROLES.admin, USER_ROLES.superAdmin].includes(Role),
        isSuperAdmin: Role === USER_ROLES.superAdmin,
        isModerator: Role === USER_ROLES.moderator,
      };
    },
    enabled: loggedIn && idleLoggedInBootstrapReady,
    placeholderData: {
      isAdmin: false,
      isSuperAdmin: false,
      isModerator: false,
    },
    gcTime: USER_ROLES_CACHE_MAX_AGE_MS,
    refetchOnWindowFocus: false,
    staleTime: USER_ROLES_CACHE_MAX_AGE_MS,
  });

  const {
    data: focusProfile = null,
    isFetched: focusProfileFetched,
    isPlaceholderData: focusProfilePlaceholder,
    refetch: refetchFocusProfile,
  } = useFocusProfileQuery({
    enabled: loggedIn,
    publicKey: currentUserPublicKey,
  });
  const activeFocusProfile =
    loggedIn && focusProfile?.PublicKeyBase58Check === currentUserPublicKey
      ? focusProfile
      : null;
  const activeFocusProfileFetched =
    focusProfileFetched && !focusProfilePlaceholder;

  useEffect(() => {
    if (
      !currentUserPublicKey ||
      !loggedIn ||
      userLoading ||
      !user ||
      !idleLoggedInBootstrapReady ||
      !activeFocusProfileFetched ||
      !activeFocusProfile
    ) {
      return;
    }

    void createGeneralDerivedKeyRef
      .current({ publicKey: currentUserPublicKey })
      .catch((error: unknown) => {
        console.warn("Failed to create general derived key", error);
      });
  }, [
    activeFocusProfile,
    activeFocusProfileFetched,
    currentUserPublicKey,
    idleLoggedInBootstrapReady,
    loggedIn,
    user,
    userLoading,
  ]);

  useEffect(() => {
    focusProfileSignal.value = activeFocusProfile;
  }, [activeFocusProfile]);

  useEffect(() => {
    if (
      loggedIn &&
      activeFocusProfileFetched &&
      !activeFocusProfile &&
      !pathname.startsWith(APP_ROUTES.signUp)
    ) {
      router.push(APP_ROUTES.signUpConnectAndEarn);
    }
  }, [
    activeFocusProfile,
    activeFocusProfileFetched,
    loggedIn,
    pathname,
    router,
  ]);

  const addUserToBlockedUsers = (publicKey: string) => {
    setBlockedUsers((prevState) => {
      const newBlockedUsers = [
        ...prevState,
        {
          target: { publicKey },
          associationType: AssociationType.block,
        } as UserAssociation,
      ];

      blockedUsersSignal.value = newBlockedUsers;
      return newBlockedUsers;
    });
  };

  const removeUserFromBlockedUsers = (publicKey: string) => {
    setBlockedUsers((prevState) => {
      const newBlockedUsers = prevState.filter(
        (association) => association.target?.publicKey !== publicKey,
      );

      blockedUsersSignal.value = newBlockedUsers;
      return newBlockedUsers;
    });
  };

  const toggleBlockUser = (publicKey: string) => {
    if (isUserBlocked(publicKey)) {
      removeUserFromBlockedUsers(publicKey);
    } else {
      addUserToBlockedUsers(publicKey);
    }
  };

  const isUserBlocked = (publicKey?: string) => {
    if (!publicKey) return false;

    return blockedUsers.some(
      (association) => association.target?.publicKey === publicKey,
    );
  };

  useEffect(() => {
    setNotificationFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!notificationCountInputsHydrated) {
      setUnreadNotifications(0);
    }
  }, [notificationCountInputsHydrated]);

  useTanstackQuery({
    queryKey: [
      "unreadNotifications",
      currentUserPublicKey,
      hiddenNotificationTypesKey,
      notificationFilters,
    ],
    queryFn: async () => {
      const params = applyHiddenNotificationTypesToParams({
        hiddenNotificationTypes,
        params: getNotificationRequestFiltersFromLocalStorage({
          filters: notificationFilters,
          params: {
            status: NotificationStatus.PROCESSED,
          },
        }),
      });

      const data = await getNotificationsRequest(params);

      startTransition(() => {
        setUnreadNotifications(data.Count);
      });

      return data.Count;
    },
    enabled: idleLoggedInBootstrapReady && notificationCountInputsHydrated,
    placeholderData: 0,
  });

  const { data: globalValData } = useQuery(GlobalValDocument, {
    fetchPolicy: "network-only",
    skip: !idleGlobalValReady,
  });
  const globalVal = globalValData?.globalValByRowId || {
    maxPostContributionWealthUsdCents: 100,
  };

  useEffect(() => {
    if ("setAppBadge" in navigator) {
      const badgePromise =
        unreadNotifications === 0
          ? navigator.clearAppBadge()
          : navigator.setAppBadge(unreadNotifications);

      badgePromise.catch((error) => {
        console.error("Failed to set app badge:", error);
      });
    }
  }, [unreadNotifications]);

  return (
    <UserContext
      value={{
        currentUserPublicKey,
        loggedIn,
        setCurrentUserPublicKey,
        alternateUsers,

        user,
        refetchUser,
        loading: userLoading || userSocialProfilesLoading,
        userLoading,
        userSocialProfilesLoading,
        userSocialProfiles,
        refetchUserSocialProfiles,
        focusProfile: activeFocusProfile,
        refetchFocusProfile,

        isAdmin,
        isModerator,
        isSuperAdmin,
        isElevated: isAdmin || isModerator,
        isVerifier: (user?.isVerifier?.totalCount ?? 0) > 0,
        toggleBlockUser,
        blockedUsers,
        isUserBlocked,
        removeUserFromBlockedUsers,
        addUserToBlockedUsers,
        setUnreadNotifications,
        unreadNotifications,

        // DESO
        realTimeDesoBalanceNanos,

        // DESO-USDC
        realTimeDusdBalanceNanos,
        realTimeDusdBaseUnits,

        // FOCUS
        realTimeFocusUnlockedBalanceNanos,
        realTimeFocusUnlockedBaseUnits,

        // FOCUS LOCKED
        realTimeFocusLockedBalanceNanos,
        realTimeFocusLockedBaseUnits,

        wealth,
        generalDerivedKey,
        createGeneralDerivedKey,
        generalDerivedKeyStatus,
        clearGeneralDerivedKey,
        authorizeGeneralDerivedKey,
        globalVal,
        ...userListsState,
        totalUnclaimedMessageTipsUsdCents,
        unreadTotalMessagesCount,
        setUnreadTotalMessagesCount,
      }}
    >
      {children}
    </UserContext>
  );
};
